import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useRtdProgress } from "@/hooks/use-rtd-progress";
import { useLocationStore } from "@/lib/store";
import {
  LevelFifteenView,
  INITIAL_LEVEL_FIFTEEN_STATE,
  derivePowerUps,
  migrateLevelFifteenState,
  computeLevelFifteenBonus,
  LEVEL_FIFTEEN_PRODUCTS,
  GOAL_TARGETS,
  LEVEL_FIFTEEN_MAX_BONUS,
  type RawLevelFifteenState,
} from "@/components/level-fifteen-view";

/**
 * LVL 15 — Total Domination endgame view.
 *
 * Gate: render this when the user has completed all Trinity tasks.
 * Current state layer: localStorage per location.
 */

const STORAGE_KEY = (locationId: string) => `lvl15_state_${locationId}`;

interface LevelFifteenPageProps {
  /** When true, suppress the internal header since the host page already has one. */
  embedded?: boolean;
}

export default function LevelFifteenPage({
  embedded = false,
}: LevelFifteenPageProps = {}) {
  const { currentLevel, gbpAnalysis } = useRtdProgress();
  const { selectedLocationId } = useLocationStore();
  const [, navigate] = useLocation();

  const { data: locations } = useQuery<any[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id || "";
  const currentLocation = locations?.find((l) => l.id === locationId);
  const businessName = currentLocation?.name || "Your Business";
  const hasWebsite = !!currentLocation?.website;

  const [state, setState] = useState<RawLevelFifteenState>(
    INITIAL_LEVEL_FIFTEEN_STATE,
  );

  // Load persisted state per location (with migration for older blobs).
  useEffect(() => {
    if (!locationId) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY(locationId));
      if (stored) {
        setState(migrateLevelFifteenState(JSON.parse(stored)));
      } else {
        setState(INITIAL_LEVEL_FIFTEEN_STATE);
      }
    } catch {
      setState(INITIAL_LEVEL_FIFTEEN_STATE);
    }
  }, [locationId]);

  // Persist on change
  useEffect(() => {
    if (!locationId) return;
    try {
      localStorage.setItem(STORAGE_KEY(locationId), JSON.stringify(state));
    } catch {
      /* quota exceeded — silently ignore */
    }
  }, [state, locationId]);

  const powerUps = useMemo(() => derivePowerUps(state), [state]);
  const bonusLevel = useMemo(
    () => computeLevelFifteenBonus(state, powerUps),
    [state, powerUps],
  );

  // Gate: Road-to-Domination Trinity must be complete to unlock the endgame.
  const TRINITY_TOTAL = 3;
  if (currentLevel < TRINITY_TOTAL) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <h2 className="text-xl font-semibold mb-2">Total Domination locked</h2>
        <p>Complete all {TRINITY_TOTAL} Trinity tasks to unlock LVL 15.</p>
        <p className="text-sm mt-2 opacity-70">
          Progress: {currentLevel}/{TRINITY_TOTAL}
        </p>
      </div>
    );
  }

  const handlePurchase = (productId: string) => {
    const product = LEVEL_FIFTEEN_PRODUCTS.find((p) => p.id === productId);
    if (!product) return;

    // Activity-log items: bump counter, latch goal-ever-met on first cross.
    if (product.goal) {
      const key = product.goal.counterKey;
      // GBP-backed counters are read-only — they reflect real Google Business
      // Profile activity from /api/locations/:id/rtd-progress. Ignore manual
      // log clicks for these so users can't inflate the count.
      if (key === "productLog" || key === "pictureLog" || key === "reviewLog") {
        return;
      }
      const target = GOAL_TARGETS[key as keyof typeof GOAL_TARGETS];
      const latchKey =
        key === "productLog"
          ? "productGoalEverMet"
          : key === "pictureLog"
            ? "pictureGoalEverMet"
            : key === "reviewLog"
              ? "reviewGoalEverMet"
              : null;

      setState((prev) => {
        const next = { ...prev, [key]: (prev[key] as number) + 1 };
        if (
          latchKey &&
          typeof target === "number" &&
          !prev[latchKey as keyof RawLevelFifteenState] &&
          (next[key] as number) >= target
        ) {
          (next as any)[latchKey] = true;
        }
        return next;
      });
      // TODO: POST to /api/locations/:id/lvl15-log { kind: key }
      return;
    }

    // Paid product — send the user to the marketplace page for this
    // product. Some products have a dedicated marketplace page (set via
    // product.route); others fall back to the generic /marketplace/:id route.
    navigate(product.route ?? `/marketplace/${product.id}`);
  };

  const handleToggleUpgrade = (key: keyof RawLevelFifteenState) => {
    setState((prev) => ({ ...prev, [key]: !prev[key] }));
    // TODO: if key === "gbpPictureUpgrade", toggle the $10/mo subscription
  };

  return (
    <LevelFifteenView
      businessName={businessName}
      state={state}
      powerUps={powerUps}
      hasWebsite={hasWebsite}
      onPurchase={handlePurchase}
      onToggleUpgrade={handleToggleUpgrade}
      embedded={embedded}
      gbpAnalysis={gbpAnalysis}
    />
  );
}

/**
 * Helper re-export so the Road-to-Domination host page can render its own
 * "LVL X / 30" badge combining base progress + endgame bonus.
 */
export { LEVEL_FIFTEEN_MAX_BONUS };
